import { useCallback, useMemo, useState } from 'react';
import { ErrorResponse } from 'react-router-dom';
import { useAsync } from 'react-use';

interface KycDocumentImage {
  fileName: string;
  fileUrl: string;
  issuingCountryCode?: string;
  savedAt: Date;
}

export type GetImagesFunction = (userId: string) => Promise<string[]>;
export type GetImageFunction = (imageId: string) => Promise<KycDocumentImage>;

export const useKYCDocumentImages = (
  userId: string,
  batchSize = 5,
  getKycDocumentImage: GetImageFunction,
  getKycDocumentImages: GetImagesFunction
) => {
  const [images, setImages] = useState<KycDocumentImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(0);

  const { value: kycDocumentImagesValue } = useAsync(async () => {
    if (!userId) return [];
    return getKycDocumentImages(userId);
  }, [userId]);

  const getKycImage = useCallback(async () => {
    if (!userId || !kycDocumentImagesValue) return [];
    setLoading(true);
    setError(null);
    const startIndex = currentPage * batchSize;
    const endIndex = Math.min(
      startIndex + batchSize,
      kycDocumentImagesValue.length
    );
    const batchUuids = kycDocumentImagesValue.slice(startIndex, endIndex);

    const promises = batchUuids.map((uuid) => getKycDocumentImage(uuid));
    try {
      const response = await Promise.all(promises);
      setImages((prevImages) => [...prevImages, ...response.map((res) => res)]);
      setCurrentPage((prevPage) => prevPage + 1);
      setLoading(false);
    } catch (e) {
      setError(e as ErrorResponse);
      setLoading(false);
    }
  }, [
    kycDocumentImagesValue,
    currentPage,
    batchSize,
    userId,
    getKycDocumentImage,
  ]);

  const hasMoreImages = useMemo(
    () => kycDocumentImagesValue?.length !== images.length,
    [images, kycDocumentImagesValue]
  );

  return { images, loading, error, hasMoreImages, loadMore: getKycImage };
};
